import Vue from 'vue'
import StrappSimProvider from '@/providers/sim/StrappSimProvider'
import DeviceService from '@/services/DeviceService'
import KnoxService from '@/services/KnoxService'
import StrappService from '@/services/StrappService'

/**
 * Helper function to fetch all paginated data from Strapp API
 * @param {Function} fetchFunction - API function to call
 * @param {Object} baseFilters - Base filters to apply
 * @returns {Promise<Array>} All results
 */
const fetchAllStrappData = (fetchFunction, baseFilters = {}) => {
  const allResults = []
  const fetchPage = (cursor = 0) => {
    const filters = { ...baseFilters, cursor }

    return fetchFunction(filters)
      .then(response => {
        const results = response.results || response.data || []
        const remaining = response.remaining || 0
        const nextCursor = response.cursor !== undefined ? response.cursor : cursor

        allResults.push(...results)

        if (remaining > 0 && results.length > 0) {
          return fetchPage(nextCursor)
        }

        return allResults
      })
  }

  return fetchPage()
}

/**
 * Helper function to fetch all paginated data from Knox API
 * @param {Function} fetchFunction - API function to call
 * @param {Object} baseFilters - Base filters to apply
 * @param {string} resultKey - Key in resultValue containing array
 * @returns {Promise<Array>} All results
 */
const fetchAllKnoxData = (fetchFunction, baseFilters = {}, resultKey = 'deviceList') => {
  const allResults = []
  const limit = baseFilters.limit || 100

  const fetchPage = (start = 0) => {
    const filters = { ...baseFilters, start, limit }

    return fetchFunction(filters)
      .then(response => {
        const resultValue = response.resultValue || {}
        const resultsFromKey = resultValue[resultKey] || resultValue.results || response.results || []
        const results = Array.isArray(resultsFromKey) ? resultsFromKey : []
        const total = resultValue.total || response.total || 0

        allResults.push(...results)

        console.log(`[fetchAllKnoxData] Fetched ${results.length} results (total: ${allResults.length}/${total})`)

        // Continuer si on a reçu le nombre maximum de résultats (page pleine)
        // ET qu'on n'a pas encore atteint le total
        if (results.length === limit && (total === 0 || allResults.length < total)) {
          return fetchPage(start + limit)
        }

        return allResults
      })
  }

  return fetchPage(baseFilters.start || 0)
}

// TTL du cache store MaterialManagement (aligné sur le cache Redis backend des routes /datas)
const MATERIAL_DATA_TTL = 5 * 60 * 1000

// Promesse en vol : dédoublonne les appels simultanés à fetchMaterialManagementData
let materialFetchInFlight = null

export default {
  namespaced: true,
  state: {
    // Devices
    allDevices: [],
    selectedDevice: null,
    devicesLoading: false,
    devicesError: null,
    lastDevicesUpdate: null,
    // Timestamp du dernier chargement complet (Knox + Strapp) réussi via /datas
    lastMaterialFetch: null,

    // Cache enrichissement avec TTL
    enrichmentCache: {
      knox: {}, // { imei: { data: {...}, timestamp: Date.now() } }
      strapp: {}, // { imei: { data: {...}, timestamp: Date.now() } }
      lastCleanup: null
    },
    enrichmentTTL: 5 * 60 * 1000, // 5 minutes en millisecondes
    enrichmentInProgress: new Set(), // Set des IMEIs en cours d'enrichissement

    // Knox specific
    knoxDevices: [],
    knoxGroups: [],
    knoxUsers: [],
    knoxAppsCatalog: [],
    knoxProfiles: [],

    // Strapp specific
    strappDevices: [],
    strappModels: [],
    strappSites: [],
    strappBillingEntities: [],
    strappUsers: [],
    strappSIMs: [],
    strappSubscriptions: [],
    strappAccessories: [],
    strappOrders: [],

    // SIM usage tracking
    simUsageMap: {},
    simUsageLoading: false,
    simUsageProgress: 0,
    simUsageLastUpdate: null,
    simUsageCancelRequested: false,

    // Filters & pagination
    deviceFilters: {
      status: null,
      user: null,
      site: null,
      model: null
    },
    currentCursor: 0,
    hasMoreDevices: true,

    // UI state
    deviceDialog: false,
    deviceActionDialog: false,
    selectedAction: null,
    actionLoading: false
  },

  mutations: {
    // Devices
    setAllDevices(state, devices) {
      state.allDevices = devices
      state.lastDevicesUpdate = Date.now()
    },
    setLastMaterialFetch(state, timestamp) {
      state.lastMaterialFetch = timestamp
    },
    setKnoxUsers(state, users) {
      state.knoxUsers = users
    },
    setKnoxGroups(state, groups) {
      state.knoxGroups = groups
    },
    appendDevices(state, devices) {
      state.allDevices = [...state.allDevices, ...devices]
    },
    setSelectedDevice(state, device) {
      state.selectedDevice = device
    },
    setDevicesLoading(state, loading) {
      state.devicesLoading = loading
    },
    setDevicesError(state, error) {
      state.devicesError = error
    },
    updateDevice(state, updatedDevice) {
      const index = state.allDevices.findIndex(device => device.imei === updatedDevice.imei)
      if (index !== -1) {
        // Utiliser Vue.set pour garantir la réactivité
        Vue.set(state.allDevices, index, updatedDevice)
      }
    },
    removeDevice(state, imei) {
      state.allDevices = state.allDevices.filter(device => device.imei !== imei)
    },

    // Cache enrichissement
    setCachedEnrichment(state, { imei, source, data }) {
      if (!state.enrichmentCache[source]) {
        state.enrichmentCache[source] = {}
      }
      state.enrichmentCache[source][imei] = {
        data,
        timestamp: Date.now()
      }
    },
    clearEnrichmentCache(state, source = null) {
      if (source) {
        state.enrichmentCache[source] = {}
      } else {
        state.enrichmentCache.knox = {}
        state.enrichmentCache.strapp = {}
      }
      state.enrichmentCache.lastCleanup = Date.now()
    },
    cleanupExpiredCache(state) {
      const now = Date.now()
      const ttl = state.enrichmentTTL
      
      Object.keys(state.enrichmentCache).forEach(source => {
        if (source === 'lastCleanup') return
        
        const cache = state.enrichmentCache[source]
        Object.keys(cache).forEach(imei => {
          if (now - cache[imei].timestamp > ttl) {
            delete cache[imei]
          }
        })
      })
      
      state.enrichmentCache.lastCleanup = now
    },
    addEnrichmentInProgress(state, imei) {
      state.enrichmentInProgress.add(imei)
    },
    removeEnrichmentInProgress(state, imei) {
      state.enrichmentInProgress.delete(imei)
    },

    // Knox
    setKnoxDevices(state, devices) {
      state.knoxDevices = devices
    },
    setKnoxAppsCatalog(state, apps) {
      state.knoxAppsCatalog = apps
    },
    setKnoxProfiles(state, profiles) {
      state.knoxProfiles = profiles
    },

    // Strapp
    setStrappDevices(state, devices) {
      state.strappDevices = devices
    },
    setStrappModels(state, models) {
      state.strappModels = models
    },
    setStrappSites(state, sites) {
      state.strappSites = sites
    },
    setStrappBillingEntities(state, entities) {
      state.strappBillingEntities = entities
    },
    setStrappUsers(state, users) {
      state.strappUsers = users
    },
    setStrappSIMs(state, sims) {
      state.strappSIMs = sims
    },
    setStrappSubscriptions(state, subscriptions) {
      state.strappSubscriptions = subscriptions
    },
    setStrappAccessories(state, accessories) {
      state.strappAccessories = accessories
    },
    setStrappOrders(state, orders) {
      state.strappOrders = orders
    },

    // SIM usage
    setSimUsageMap(state, usageMap) {
      state.simUsageMap = usageMap
    },
    updateSimUsageEntry(state, { simId, usage }) {
      state.simUsageMap = { ...state.simUsageMap, [simId]: usage }
    },
    setSimUsageLoading(state, loading) {
      state.simUsageLoading = loading
    },
    setSimUsageProgress(state, progress) {
      state.simUsageProgress = progress
    },
    setSimUsageLastUpdate(state, timestamp) {
      state.simUsageLastUpdate = timestamp
    },
    setSimUsageCancelRequested(state, requested) {
      state.simUsageCancelRequested = requested
    },
    clearSimUsageCancel(state) {
      state.simUsageCancelRequested = false
    },

    // Filters & pagination
    setDeviceFilters(state, filters) {
      state.deviceFilters = { ...state.deviceFilters, ...filters }
    },
    resetDeviceFilters(state) {
      state.deviceFilters = {
        status: null,
        user: null,
        site: null,
        model: null
      }
    },
    setCurrentCursor(state, cursor) {
      state.currentCursor = cursor
    },
    setHasMoreDevices(state, hasMore) {
      state.hasMoreDevices = hasMore
    },

    // UI
    setDeviceDialog(state, show) {
      state.deviceDialog = show
    },
    setDeviceActionDialog(state, show) {
      state.deviceActionDialog = show
    },
    setSelectedAction(state, action) {
      state.selectedAction = action
    },
    setActionLoading(state, loading) {
      state.actionLoading = loading
    }
  },

  actions: {
    /**
     * Charge tous les devices (Knox + Strapp fusionnés)
     */
    fetchAllDevices({ commit }, { forceRefresh = false } = {}) {
      commit('setDevicesLoading', true)
      commit('setDevicesError', null)

      return DeviceService.getAllDevices({ forceRefresh })
        .then(devices => {
          commit('setAllDevices', devices)
          commit('setDevicesLoading', false)
          return devices
        })
        .catch(error => {
          console.error('[Store] Error fetching all devices:', error)
          commit('setDevicesError', error.message || 'Error fetching devices')
          commit('setDevicesLoading', false)
          throw error
        })
    },

    /**
     * Charge un device par IMEI
     */
    fetchDeviceByImei({ commit }, { imei, forceRefresh = false }) {
      commit('setDevicesLoading', true)
      
      return DeviceService.getDeviceByImei(imei, forceRefresh)
        .then(device => {
          commit('setSelectedDevice', device)
          commit('updateDevice', device)
          commit('setDevicesLoading', false)
          return device
        })
        .catch(error => {
          console.error(`[Store] Error fetching device by IMEI ${imei}:`, error)
          commit('setDevicesError', error.message)
          commit('setDevicesLoading', false)
          throw error
        })
    },

    /**
     * Enrichit les devices avec les données Strapp via IMEI
     * Utilise le cache pour éviter les appels répétés
     */
    enrichDevicesWithStrapp({ state, commit, getters }) {
      const BATCH_SIZE = 50
      const BATCH_DELAY_MS = 3000
      
      // D'abord, appliquer le cache à tous les devices qui en ont un
      let cacheAppliedCount = 0
      state.allDevices.forEach(device => {
        const imei = device.imei
        if (!imei) return
        
        // Si pas encore de strappData et qu'il y a un cache valide, l'appliquer
        if ((!device.strappData || Object.keys(device.strappData).length === 0)) {
          const cached = getters.getCachedEnrichment(imei, 'strapp')
          if (cached) {
            console.log(`[Store] Applying cached Strapp data for ${imei}`)
            const enrichedDevice = {
              ...device,
              strappData: cached,
              status: cached.status || null,
              engagementDurationMonths: cached.engagementDurationMonths || null,
              endOfEngagementDate: cached.endOfEngagementDate || null,
              monthlyPrice: cached.monthlyPrice || null,
              site: cached.site || null,
              billingEntity: cached.billingEntity || null
            }
            commit('updateDevice', enrichedDevice)
            cacheAppliedCount++
          }
        }
      })
      
      if (cacheAppliedCount > 0) {
        console.log(`[Store] Applied cached Strapp data to ${cacheAppliedCount} devices`)
      }
      
      // Ensuite, identifier les devices qui ont besoin d'enrichissement
      const devicesWithoutStrapp = state.allDevices.filter(d => {
        const imei = d.imei
        if (!imei) return false
        
        // Vérifier si déjà enrichi avec strappData
        if (d.strappData && Object.keys(d.strappData).length > 0) {
          return false
        }
        
        return true
      })
      
      if (devicesWithoutStrapp.length === 0) {
        console.log('[Store] No devices to enrich with Strapp (all cached or already enriched)')
        return Promise.resolve()
      }
      
      const totalBatches = Math.ceil(devicesWithoutStrapp.length / BATCH_SIZE)
      console.log(`[Store] Enriching ${devicesWithoutStrapp.length} devices with Strapp in ${totalBatches} batches`)
      
      const processBatch = (batchIndex) => {
        const start = batchIndex * BATCH_SIZE
        const batch = devicesWithoutStrapp.slice(start, start + BATCH_SIZE)
        
        if (batch.length === 0) {
          return Promise.resolve()
        }
        
        return Promise.all(
          batch.map(device => {
            const imei = device.imei
            
            // Double vérification cache (au cas où enrichi entre-temps par un autre batch)
            const cached = getters.getCachedEnrichment(imei, 'strapp')
            if (cached) {
              const enrichedDevice = {
                ...device,
                strappData: cached,
                status: cached.status || null,
                engagementDurationMonths: cached.engagementDurationMonths || null,
                endOfEngagementDate: cached.endOfEngagementDate || null,
                monthlyPrice: cached.monthlyPrice || null,
                site: cached.site || null,
                billingEntity: cached.billingEntity || null
              }
              commit('updateDevice', enrichedDevice)
              return Promise.resolve({ imei, found: true, cached: true })
            }
            
            // Marquer comme en cours
            commit('addEnrichmentInProgress', imei)
            
            return StrappService.getDevices({ 
              imei, 
              limit: 1,
              populate: 'user,model,site,billingEntity'
            })
              .then(response => {
                commit('removeEnrichmentInProgress', imei)
                
                const strappDevice = response.results && response.results.length > 0 ? response.results[0] : null
                if (strappDevice) {
                  // Mettre en cache
                  commit('setCachedEnrichment', {
                    imei,
                    source: 'strapp',
                    data: strappDevice
                  })
                  
                  const enrichedDevice = {
                    ...device,
                    strappData: strappDevice,
                    status: strappDevice.status || null,
                    engagementDurationMonths: strappDevice.engagementDurationMonths || null,
                    endOfEngagementDate: strappDevice.endOfEngagementDate || null,
                    monthlyPrice: strappDevice.monthlyPrice || null,
                    site: strappDevice.site || null,
                    billingEntity: strappDevice.billingEntity || null
                  }
                  commit('updateDevice', enrichedDevice)
                } else {
                  // Même si pas trouvé, marquer comme enrichi avec des valeurs null
                  const enrichedDevice = {
                    ...device,
                    strappData: {},
                    status: null,
                    engagementDurationMonths: null,
                    endOfEngagementDate: null,
                    monthlyPrice: null
                  }
                  commit('updateDevice', enrichedDevice)
                }
                return { imei, found: !!strappDevice, cached: false }
              })
              .catch(error => {
                commit('removeEnrichmentInProgress', imei)
                console.error(`  ❌ Error for IMEI ${imei}:`, error.message)
                return { imei, found: false, error }
              })
          })
        )
          .then(results => {
            const foundCount = results.filter(r => r.found).length
            const cachedCount = results.filter(r => r.cached).length
            console.log(`[Store] Batch ${batchIndex + 1}/${totalBatches}: ${foundCount} found (${cachedCount} cached)`)
            
            if (batchIndex + 1 < totalBatches) {
              return new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS))
                .then(() => processBatch(batchIndex + 1))
            }
          })
      }
      
      return processBatch(0)
    },

    /**
     * Enrichit les devices avec les détails Knox complets via /knox/devices/{deviceId}
     * Utilise le cache pour éviter les appels répétés
     */
    enrichDevicesWithKnoxDetails({ state, commit, getters }) {
      const devicesWithKnoxId = state.allDevices.filter(d => d.knoxData?.deviceId || d.deviceId)
      
      if (devicesWithKnoxId.length === 0) {
        console.log('[Store] No devices with Knox ID to enrich')
        return Promise.resolve()
      }
      
      // Nettoyer le cache expiré périodiquement
      const now = Date.now()
      if (!state.enrichmentCache.lastCleanup || now - state.enrichmentCache.lastCleanup > 60000) {
        commit('cleanupExpiredCache')
      }
      
      console.log(`[Store] Enriching ${devicesWithKnoxId.length} devices with Knox details...`)
      
      const enrichmentPromises = devicesWithKnoxId.map(device => {
        const deviceId = device.knoxData?.deviceId || device.deviceId
        const imei = device.imei
        
        // Vérifier le cache
        const cached = getters.getCachedEnrichment(imei, 'knox')
        if (cached) {
          console.log(`[Store] Using cached Knox data for ${imei}`)
          const enrichedDevice = {
            ...device,
            knoxData: { ...(device.knoxData || {}), ...cached },
            battery: cached.battery,
            deviceVersionName: cached.deviceVersionName
          }
          commit('updateDevice', enrichedDevice)
          return Promise.resolve({ deviceId, success: true, cached: true })
        }
        
        // Vérifier si enrichissement en cours
        if (getters.isEnrichmentInProgress(imei)) {
          console.log(`[Store] Enrichment already in progress for ${imei}`)
          return Promise.resolve({ deviceId, success: false, inProgress: true })
        }
        
        // Marquer comme en cours
        commit('addEnrichmentInProgress', imei)
        
        return KnoxService.getDeviceById(deviceId, false)
          .then(response => {
            commit('removeEnrichmentInProgress', imei)
            
            if (response.resultCode === '0' && response.resultValue) {
              // Mettre en cache
              commit('setCachedEnrichment', {
                imei,
                source: 'knox',
                data: response.resultValue
              })
              
              const enrichedKnoxData = { ...(device.knoxData || {}), ...response.resultValue }
              const enrichedDevice = {
                ...device,
                knoxData: enrichedKnoxData,
                battery: response.resultValue.battery,
                deviceVersionName: response.resultValue.deviceVersionName
              }
              commit('updateDevice', enrichedDevice)
              return { deviceId, success: true, cached: false }
            }
            return { deviceId, success: false }
          })
          .catch(error => {
            commit('removeEnrichmentInProgress', imei)
            console.warn(`[Store] Failed to enrich device ${deviceId}:`, error)
            return { deviceId, success: false, error }
          })
      })
      
      return Promise.all(enrichmentPromises)
        .then(results => {
          const successCount = results.filter(r => r.success).length
          const cachedCount = results.filter(r => r.cached).length
          console.log(`[Store] Knox details enrichment complete: ${successCount}/${results.length} succeeded (${cachedCount} from cache)`)
          return results
        })
    },

    /**
     * Charge les devices d'un utilisateur Knox
     */
    fetchDevicesByKnoxUser({ commit }, userId) {
      commit('setDevicesLoading', true)
      
      return DeviceService.getDevicesByKnoxUser(userId)
        .then(devices => {
          commit('setAllDevices', devices)
          commit('setDevicesLoading', false)
          return devices
        })
        .catch(error => {
          console.error(`[Store] Error fetching devices for Knox user ${userId}:`, error)
          commit('setDevicesError', error.message)
          commit('setDevicesLoading', false)
          throw error
        })
    },

    /**
     * KNOX ACTIONS
     */

    fetchKnoxDevices({ commit }, filters = {}) {
      // Utiliser /datas pour récupérer tous les devices en un seul appel
      return KnoxService.getDatas(filters.force || false)
        .then(response => {
          const devices = response.devices || []
          console.log(`[Store] Fetched ${devices.length} Knox devices from /datas`)
          
          commit('setKnoxDevices', devices)
          return { deviceList: devices, total: devices.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Knox devices:', error)
          throw error
        })
    },

    enrichKnoxDevicesWithDetails({ commit, state }) {
      const devices = state.knoxDevices
      if (!devices || devices.length === 0) {
        return Promise.resolve([])
      }

      console.log(`[Store] Enriching ${devices.length} Knox devices with full details...`)
      
      const enrichmentPromises = devices.map(device => 
        KnoxService.getDeviceById(device.deviceId, false)
          .then(response => {
            if (response.resultCode === '0' && response.resultValue) {
              return { ...device, ...response.resultValue }
            }
            return device
          })
          .catch(error => {
            console.warn(`[Store] Failed to enrich device ${device.deviceId}:`, error)
            return device
          })
      )

      return Promise.all(enrichmentPromises)
        .then(enrichedDevices => {
          commit('setKnoxDevices', enrichedDevices)
          const enrichedByImei = enrichedDevices.reduce((accumulator, knoxDevice) => {
            if (!knoxDevice?.imei) return accumulator
            return { ...accumulator, [knoxDevice.imei]: knoxDevice }
          }, {})

          const updatedAllDevices = state.allDevices.map(device => {
            const enrichedKnox = enrichedByImei[device.imei]
            if (!enrichedKnox) return device
            return {
              ...device,
              knoxData: enrichedKnox,
              battery: enrichedKnox.battery ?? device.battery,
              deviceVersionName: enrichedKnox.deviceVersionName ?? device.deviceVersionName
            }
          })

          commit('setAllDevices', updatedAllDevices)
          console.log('[Store] Knox devices enriched successfully')
          return enrichedDevices
        })
    },

    fetchKnoxGroups({ commit }, filters = {}) {
      return fetchAllKnoxData(KnoxService.getGroups, filters, 'groups')
        .then(allResults => {
          commit('setKnoxGroups', allResults)
          return { groups: allResults, total: allResults.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Knox groups:', error)
          throw error
        })
    },

    fetchKnoxUsers({ commit }, filters = {}) {
      // Knox users have special nested structure: resultValue.users.user
      return KnoxService.getUsers(filters)
        .then(response => {
          const resultValue = response.resultValue || {}
          const usersData = resultValue.users || {}
          const userList = usersData.user || []
          const total = resultValue.total || userList.length
          
          commit('setKnoxUsers', userList)
          return { users: userList, total }
        })
        .catch(error => {
          console.error('[Store] Error fetching Knox users:', error)
          throw error
        })
    },

    fetchKnoxAppsCatalog({ commit }, filters = {}) {
      return KnoxService.getAppsCatalog(filters)
        .then(response => {
          commit('setKnoxAppsCatalog', response.resultValue?.apps || [])
          return response
        })
        .catch(error => {
          console.error('[Store] Error fetching Knox apps catalog:', error)
          throw error
        })
    },

    fetchKnoxProfiles({ commit }, filters = {}) {
      return KnoxService.getKMEProfiles(filters)
        .then(response => {
          commit('setKnoxProfiles', response.profiles || [])
          return response
        })
        .catch(error => {
          console.error('[Store] Error fetching Knox profiles:', error)
          throw error
        })
    },

    /**
     * Commandes Knox
     */

    lockDevice({ commit, dispatch }, { deviceId, message = '' }) {
      commit('setActionLoading', true)
      
      return DeviceService.lockDevice(deviceId, message)
        .then(result => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: 'Device verrouillé avec succès', 
            color: 'success' 
          }, { root: true })
          return result
        })
        .catch(error => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: `Erreur lors du verrouillage: ${error.message}`, 
            color: 'error' 
          }, { root: true })
          throw error
        })
    },

    unlockDevice({ commit, dispatch }, deviceId) {
      commit('setActionLoading', true)
      
      return DeviceService.unlockDevice(deviceId)
        .then(result => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: 'Device déverrouillé avec succès', 
            color: 'success' 
          }, { root: true })
          return result
        })
        .catch(error => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: `Erreur lors du déverrouillage: ${error.message}`, 
            color: 'error' 
          }, { root: true })
          throw error
        })
    },

    rebootDevice({ commit, dispatch }, deviceId) {
      commit('setActionLoading', true)
      
      return DeviceService.rebootDevice(deviceId)
        .then(result => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: 'Redémarrage du device initié', 
            color: 'success' 
          }, { root: true })
          return result
        })
        .catch(error => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: `Erreur lors du redémarrage: ${error.message}`, 
            color: 'error' 
          }, { root: true })
          throw error
        })
    },

    sendNotification({ commit, dispatch }, { deviceId, title, message, sendType }) {
      commit('setActionLoading', true)
      
      return DeviceService.sendNotification(deviceId, title, message, sendType)
        .then(result => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: 'Notification envoyée au device', 
            color: 'success' 
          }, { root: true })
          return result
        })
        .catch(error => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: `Erreur lors de l'envoi: ${error.message}`, 
            color: 'error' 
          }, { root: true })
          throw error
        })
    },

    collectGPS({ commit, dispatch }, deviceId) {
      commit('setActionLoading', true)
      
      return DeviceService.collectGPS(deviceId)
        .then(result => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: 'Collecte GPS initiée', 
            color: 'success' 
          }, { root: true })
          return result
        })
        .catch(error => {
          commit('setActionLoading', false)
          dispatch('addNotification', { 
            text: `Erreur lors de la collecte GPS: ${error.message}`, 
            color: 'error' 
          }, { root: true })
          throw error
        })
    },

    /**
     * STRAPP ACTIONS
     */

    fetchStrappDevices({ commit }, filters = {}) {
      return StrappService.getDevices(filters)
        .then(response => {
          commit('setStrappDevices', response.data || [])
          return response
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp devices:', error)
          throw error
        })
    },

    fetchStrappModels({ commit }, filters = {}) {
      return fetchAllStrappData(StrappService.getModels, filters)
        .then(allResults => {
          commit('setStrappModels', allResults)
          return { results: allResults, count: allResults.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp models:', error)
          throw error
        })
    },

    fetchStrappSites({ commit }, filters = {}) {
      // Ajouter populate pour l'adresse
      const enrichedFilters = {
        ...filters,
        populate: filters.populate || ['address']
      }
      return fetchAllStrappData(StrappService.getSites, enrichedFilters)
        .then(allResults => {
          commit('setStrappSites', allResults)
          return { results: allResults, count: allResults.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp sites:', error)
          throw error
        })
    },

    fetchStrappBillingEntities({ commit }, filters = {}) {
      return fetchAllStrappData(StrappService.getBillingEntities, filters)
        .then(allResults => {
          commit('setStrappBillingEntities', allResults)
          return { results: allResults, count: allResults.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp billing entities:', error)
          throw error
        })
    },

    fetchStrappUsers({ commit }, filters = {}) {
      // Ajouter populate pour l'adresse
      const enrichedFilters = {
        ...filters,
        populate: filters.populate || ['address', 'site']
      }
      return fetchAllStrappData(StrappService.getUsers, enrichedFilters)
        .then(allResults => {
          commit('setStrappUsers', allResults)
          return { results: allResults, count: allResults.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp users:', error)
          throw error
        })
    },

    fetchStrappUserById(_context, userId) {
      return StrappService.getUserById(userId)
        .then(user => {
          return user
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp user by id:', error)
          throw error
        })
    },

    updateStrappUser(_context, { userId, userData }) {
      return StrappService.updateUser(userId, userData)
        .then(updatedUser => {
          return updatedUser
        })
        .catch(error => {
          console.error('[Store] Error updating Strapp user:', error)
          throw error
        })
    },

    fetchStrappSIMs({ commit }, filters = {}) {
      // Ajouter populate par défaut pour avoir subscription, user, device
      // device est crucial pour le matching SIM-Device via IMEI
      const enrichedFilters = {
        ...filters,
        populate: filters.populate || 'subscription,user,site,billingEntity,device'
      }
      return fetchAllStrappData(StrappService.getSIMs, enrichedFilters)
        .then(allResults => {
          // Enrichir les SIMs brutes avec les champs normalisés du provider
          // On garde les données brutes ET on ajoute assignedDevice
          const enrichedSims = allResults.map(rawSim => {
            const normalized = StrappSimProvider.normalizeSim(rawSim)
            return {
              ...rawSim,  // Garder toutes les données brutes
              assignedDevice: normalized.assignedDevice,  // Ajouter le champ normalisé IMEI
              provider: normalized.provider
            }
          })
          
          // Log pour vérifier que assignedDevice est bien extrait
          const withDevice = enrichedSims.filter(s => s.assignedDevice).length
          if (withDevice > 0) {
            console.log(`[Store] ${withDevice}/${enrichedSims.length} SIMs ont un device assigné`)
          }
          
          commit('setStrappSIMs', enrichedSims)
          return { results: enrichedSims, count: enrichedSims.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp SIMs:', error)
          throw error
        })
    },

    /**
     * Charge toutes les données pour MaterialManagement en 2 appels optimisés
     * Utilise les routes agrégées /knox/datas et /strapp/datas avec cache backend
     *
     * Optimisations front :
     * - cache store 5 min : navigation retour sans appel réseau (bypass avec forceRefresh)
     * - dédoublonnage des appels simultanés (created + activated, double clic...)
     * - commits indépendants Knox / Strapp : les tablettes s'affichent dès la première
     *   réponse, l'autre source complète le merge à son arrivée (colonnes en skeleton)
     */
    fetchMaterialManagementData({ state, commit }, { forceRefresh = false } = {}) {
      if (materialFetchInFlight) {
        return materialFetchInFlight
      }

      if (!forceRefresh && state.lastMaterialFetch && (Date.now() - state.lastMaterialFetch) < MATERIAL_DATA_TTL && state.allDevices.length > 0) {
        console.log('[Store] Material Management data served from store cache')
        return Promise.resolve({ cached: true, devices: state.allDevices, sims: state.strappSIMs })
      }

      commit('setDevicesLoading', true)

      console.log('[Store] Fetching Material Management data with forceRefresh:', forceRefresh)

      const pickArray = (...candidates) => candidates.find(candidate => Array.isArray(candidate)) || []

      // Chaque source alimente le merge dès qu'elle répond ; l'autre côté vaut []
      // tant qu'il n'a pas répondu (strappData null → skeletons dans la table)
      let knoxDevices = null
      let strappDevices = null
      const commitMergedDevices = () => {
        commit('setAllDevices', DeviceService._mergeDevicesLists(knoxDevices || [], strappDevices || []))
      }

      const knoxPromise = KnoxService.getDatas(forceRefresh).then(knoxData => {
        knoxDevices = pickArray(
          knoxData.devices?.deviceList,
          knoxData.devices?.results,
          knoxData.devices,
          knoxData.resultValue?.deviceList,
          knoxData.results?.devices
        )

        const knoxUsers = pickArray(
          knoxData.users?.user,
          knoxData.users?.users?.user,
          knoxData.users,
          knoxData.resultValue?.users?.user,
          knoxData.resultValue?.users
        )
        const knoxGroups = pickArray(
          knoxData.groups?.group,
          knoxData.groups?.groups?.group,
          knoxData.groups,
          knoxData.resultValue?.groups
        )

        // Enrichir les devices avec les champs reserved des utilisateurs Knox
        // (avant merge : les objets knoxData du merge référencent ces devices)
        console.log(`[Store] Enriching ${knoxDevices.length} devices with ${knoxUsers.length} users data`)
        const usersMap = knoxUsers.reduce((map, user) => {
          if (user.userId) {
            map[user.userId] = user
          }
          return map
        }, {})

        knoxDevices.forEach(device => {
          const user = device.userId && usersMap[device.userId]
          if (user) {
            device.reserved01 = user.reserved01
            device.reserved02 = user.reserved02
            device.reserved03 = user.reserved03
          }
        })

        commit('setKnoxDevices', knoxDevices)
        commit('setKnoxUsers', knoxUsers)
        commit('setKnoxGroups', knoxGroups)
        commitMergedDevices()

        return knoxData
      })

      const strappPromise = StrappService.getDatas(forceRefresh).then(strappData => {
        strappDevices = pickArray(
          strappData.devices?.results,
          strappData.devices,
          strappData.results?.devices
        )

        const rawSims = pickArray(
          strappData.sims?.results,
          strappData.sims,
          strappData.results?.sims
        )

        // Enrichir les SIMs avec assignedDevice, quota et conso (si fournis par /datas)
        const sims = rawSims.map(rawSim => {
          const subscription = rawSim.subscription ? { ...rawSim.subscription } : null
          const subscriptionDataMo = subscription?.dataMo || (subscription?.dataPackage ? subscription.dataPackage * 1000 : null)
          if (subscription && subscriptionDataMo) {
            subscription.dataMo = subscriptionDataMo
          }

          const usageTotalFromRoot = rawSim.totalDataMo ?? rawSim.dataMo ?? rawSim.currentDataUsageMo ?? rawSim.currentDataUsage ?? rawSim.usageMo
          const usagePeriodFromRoot = rawSim.periodDataMo ?? rawSim.currentPeriodDataMo
          const hasUsageTotalFromRoot = usageTotalFromRoot !== undefined && usageTotalFromRoot !== null
          const hasUsagePeriodFromRoot = usagePeriodFromRoot !== undefined && usagePeriodFromRoot !== null
          const hasUsageArray = Array.isArray(rawSim.usage) && rawSim.usage.length > 0
          const usageFromArray = hasUsageArray
            ? rawSim.usage.reduce((sum, entry) => sum + (entry.dataMo || 0), 0)
            : null

          const usage = rawSim.usage && !Array.isArray(rawSim.usage) ? {
            totalDataMo: rawSim.usage.totalDataMo ?? usageTotalFromRoot ?? 0,
            periodDataMo: rawSim.usage.periodDataMo ?? usagePeriodFromRoot ?? 0
          } : (hasUsageArray || hasUsageTotalFromRoot || hasUsagePeriodFromRoot) ? {
            totalDataMo: usageFromArray ?? usageTotalFromRoot ?? 0,
            periodDataMo: usagePeriodFromRoot ?? 0
          } : null

          return {
            ...rawSim,
            subscription,
            usage,
            quotaMo: rawSim.quotaMo || subscriptionDataMo || 0,
            assignedDevice: rawSim.device?.imei || null,
            provider: 'strapp'
          }
        })

        const simUsageMap = {}
        sims.forEach(sim => {
          if (sim.id && sim.usage) {
            simUsageMap[sim.id] = {
              totalDataMo: sim.usage.totalDataMo || 0,
              periodDataMo: sim.usage.periodDataMo || 0
            }
          }
        })

        const subscriptions = pickArray(
          strappData.subscriptions?.results,
          strappData.subscriptions,
          strappData.results?.subscriptions
        )
        const models = pickArray(
          strappData.models?.results,
          strappData.models,
          strappData.results?.models
        )
        const sites = pickArray(
          strappData.sites?.results,
          strappData.sites,
          strappData.results?.sites
        )
        const billingEntities = pickArray(
          strappData.billingEntities?.results,
          strappData.billingEntities,
          strappData.results?.billingEntities
        )
        const accessories = pickArray(
          strappData.accessories?.results,
          strappData.accessories,
          strappData.results?.accessories
        )
        const orders = pickArray(
          strappData.orders?.results,
          strappData.orders,
          strappData.results?.orders
        )

        commit('setStrappDevices', strappDevices)
        commit('setStrappSIMs', sims)
        commit('setStrappSubscriptions', subscriptions)
        commit('setStrappModels', models)
        commit('setStrappSites', sites)
        commit('setStrappBillingEntities', billingEntities)
        commit('setStrappAccessories', accessories)
        commit('setStrappOrders', orders)
        commit('setSimUsageMap', simUsageMap)
        if (Object.keys(simUsageMap).length > 0) {
          commit('setSimUsageLastUpdate', Date.now())
        }
        commitMergedDevices()

        return strappData
      })

      materialFetchInFlight = Promise.allSettled([knoxPromise, strappPromise])
        .then(([knoxResult, strappResult]) => {
          materialFetchInFlight = null
          commit('setDevicesLoading', false)

          if (knoxResult.status === 'rejected' && strappResult.status === 'rejected') {
            console.error('[Store] Error loading Material Management data:', knoxResult.reason)
            throw knoxResult.reason
          }
          if (knoxResult.status === 'rejected') {
            console.error('[Store] Knox /datas failed, affichage Strapp seul:', knoxResult.reason)
          }
          if (strappResult.status === 'rejected') {
            console.error('[Store] Strapp /datas failed, affichage Knox seul:', strappResult.reason)
          }

          // Cache store validé uniquement si les 2 sources ont répondu :
          // en cas d'échec partiel, la prochaine visite retente le chargement
          if (knoxResult.status === 'fulfilled' && strappResult.status === 'fulfilled') {
            commit('setLastMaterialFetch', Date.now())
          }

          return {
            devices: state.allDevices,
            sims: state.strappSIMs,
            knox: knoxResult.status === 'fulfilled' ? knoxResult.value : null,
            strapp: strappResult.status === 'fulfilled' ? strappResult.value : null,
            partialError: knoxResult.reason || strappResult.reason || null
          }
        })

      return materialFetchInFlight
    },

    fetchStrappSubscriptions({ commit }, filters = {}) {
      return fetchAllStrappData(StrappService.getSubscriptions, filters)
        .then(allResults => {
          commit('setStrappSubscriptions', allResults)
          return { results: allResults, count: allResults.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp subscriptions:', error)
          throw error
        })
    },

    fetchStrappAccessories({ commit }, filters = {}) {
      return fetchAllStrappData(StrappService.getAccessories, filters)
        .then(allResults => {
          commit('setStrappAccessories', allResults)
          return { results: allResults, count: allResults.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp accessories:', error)
          throw error
        })
    },

    fetchStrappOrders({ commit }, filters = {}) {
      const populatedFilters = {
        ...filters,
        populate: ['address', 'billingEntity', 'deliverySite', 'items']
      }
      
      return fetchAllStrappData(StrappService.getOrders, populatedFilters)
        .then(allResults => {
          commit('setStrappOrders', allResults)
          return { results: allResults, count: allResults.length }
        })
        .catch(error => {
          console.error('[Store] Error fetching Strapp orders:', error)
          throw error
        })
    },

    /**
     * Actions SIM Strapp
     */

    /**
     * Charge la consommation de tous les SIMs en batch avec rate limiting
     * @param {Object} options - Options de chargement
     * @param {boolean} options.forceRefresh - Force le rechargement même si cache valide
     * @param {number} options.cacheMaxAge - Age maximum du cache en ms (défaut: 1 heure)
     */
    loadSimsUsageInBatch({ state, commit, dispatch }, { forceRefresh = false, cacheMaxAge = 3600000 } = {}) {
      // Check cache validity
      const now = Date.now()
      if (!forceRefresh && state.simUsageLastUpdate && (now - state.simUsageLastUpdate) < cacheMaxAge) {
        return Promise.resolve({ cached: true, alertsCount: 0 })
      }

      // Check if already loading
      if (state.simUsageLoading) {
        return Promise.resolve({ alreadyLoading: true })
      }

      const sims = state.strappSIMs || []
      if (sims.length === 0) {
        return Promise.resolve({ noSims: true })
      }

      // Initialize
      commit('clearSimUsageCancel')
      commit('setSimUsageLoading', true)
      commit('setSimUsageProgress', 0)
      
      const BATCH_SIZE = 100
      const BATCH_DELAY_MS = 6000
      const totalSims = sims.length
      const startTime = Date.now()

      const loadBatch = (startIndex) => {
        // Check if cancel requested
        if (state.simUsageCancelRequested) {
          commit('setSimUsageLoading', false)
          commit('setSimUsageProgress', 0)
          commit('clearSimUsageCancel')
          return Promise.resolve({ cancelled: true })
        }

        if (startIndex >= totalSims) {
          // All done
          const duration = ((Date.now() - startTime) / 1000).toFixed(1)
          
          commit('setSimUsageProgress', 100)
          commit('setSimUsageLastUpdate', Date.now())
          
          // Count alerts (≥80%)
          const alertsCount = Object.entries(state.simUsageMap)
            .filter(([simId, usage]) => {
              const sim = sims.find(s => s.id === simId)
              if (!sim?.subscription?.dataMo) return false
              const percentage = (usage.totalDataMo / sim.subscription.dataMo) * 100
              return percentage >= 80
            })
            .length

          // Keep progress at 100% for 2 seconds
          setTimeout(() => {
            commit('setSimUsageLoading', false)
            commit('setSimUsageProgress', 0)
          }, 2000)

          // Dispatch notification
          if (alertsCount > 0) {
            dispatch('addNotification', {
              text: `${alertsCount} site(s) ont une consommation ≥80%`,
              color: 'warning'
            }, { root: true })
          } else {
            dispatch('addNotification', {
              text: 'Consommation SIM chargée - Aucune alerte',
              color: 'info'
            }, { root: true })
          }

          return Promise.resolve({ alertsCount, duration })
        }

        const batch = sims.slice(startIndex, startIndex + BATCH_SIZE)
        const moment = require('moment')
        const promises = batch.map(sim => {
          return StrappService.getSIMUsage({
            simId: sim.id,
            from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
            to: moment().format('YYYY-MM-DD'),
            aggregation: 'monthly'
          })
            .then(response => {
              const results = response.results || []
              const totalDataMo = results.reduce((sum, entry) => sum + (entry.dataMo || 0), 0)
              commit('updateSimUsageEntry', { simId: sim.id, usage: { totalDataMo } })
              return { simId: sim.id, success: true }
            })
            .catch(error => {
              console.error(`[Store] Error loading usage for SIM ${sim.id}:`, error)
              return { simId: sim.id, success: false, error }
            })
        })

        return Promise.all(promises)
          .then(() => {
            const processedSims = startIndex + batch.length
            const progress = Math.min(100, Math.round((processedSims / totalSims) * 100))
            commit('setSimUsageProgress', progress)
            
            // Continue with next batch after delay
            if (processedSims < totalSims) {
              return new Promise(resolve => {
                setTimeout(() => {
                  loadBatch(processedSims).then(resolve)
                }, BATCH_DELAY_MS)
              })
            } else {
              return loadBatch(processedSims)
            }
          })
      }

      return loadBatch(0)
    },

    /**
     * Annule le chargement en cours des SIM usage
     */
    cancelSimsUsageLoading({ commit }) {
      commit('setSimUsageCancelRequested', true)
    },

    activateSIM({ dispatch }, activationData) {
      return StrappService.activateSIM(activationData)
        .then(result => {
          dispatch('addNotification', { 
            text: 'Demande d\'activation SIM enregistrée', 
            color: 'success' 
          }, { root: true })
          return result
        })
        .catch(error => {
          dispatch('addNotification', { 
            text: `Erreur lors de l'activation: ${error.message}`, 
            color: 'error' 
          }, { root: true })
          throw error
        })
    },

    cancelSIM({ dispatch }, cancelData) {
      return StrappService.cancelSIM(cancelData)
        .then(result => {
          dispatch('addNotification', { 
            text: 'Demande de résiliation SIM enregistrée', 
            color: 'success' 
          }, { root: true })
          return result
        })
        .catch(error => {
          dispatch('addNotification', { 
            text: `Erreur lors de la résiliation: ${error.message}`, 
            color: 'error' 
          }, { root: true })
          throw error
        })
    },

    createOrder({ dispatch }, orderData) {
      return StrappService.createOrder(orderData)
        .then(result => {
          dispatch('addNotification', {
            text: 'Commande créée avec succès',
            color: 'success'
          }, { root: true })
          return result
        })
        .catch(error => {
          // Timeout amont : la commande a probablement abouti. Le message backend
          // est déjà explicite (vérifier la liste, risque de doublon) → warning,
          // pas error. Notification unique : les dialogs ne doublent plus.
          const isTimeout = /Délai dépassé|peut-être quand même|timeout/i.test(error.message || '')
          dispatch('addNotification', {
            text: isTimeout ? error.message : `Erreur lors de la création de commande: ${error.message}`,
            color: isTimeout ? 'warning' : 'error'
          }, { root: true })
          throw error
        })
    },

    initiateBuyback({ dispatch }, deviceId) {
      return StrappService.createBuyback(deviceId)
        .then(result => {
          dispatch('addNotification', { 
            text: 'Demande de rachat initiée', 
            color: 'success' 
          }, { root: true })
          return result
        })
        .catch(error => {
          dispatch('addNotification', { 
            text: `Erreur lors de l'initiation du rachat: ${error.message}`, 
            color: 'error' 
          }, { root: true })
          throw error
        })
    },

    /**
     * UI actions
     */

    openDeviceDialog({ commit }, device) {
      commit('setSelectedDevice', device)
      commit('setDeviceDialog', true)
    },

    closeDeviceDialog({ commit }) {
      commit('setDeviceDialog', false)
      commit('setSelectedDevice', null)
    },

    openDeviceActionDialog({ commit }, { device, action }) {
      commit('setSelectedDevice', device)
      commit('setSelectedAction', action)
      commit('setDeviceActionDialog', true)
    },

    closeDeviceActionDialog({ commit }) {
      commit('setDeviceActionDialog', false)
      commit('setSelectedDevice', null)
      commit('setSelectedAction', null)
    },

    setDeviceFilters({ commit }, filters) {
      commit('setDeviceFilters', filters)
    },

    resetDeviceFilters({ commit }) {
      commit('resetDeviceFilters')
    },

    /**
     * Force le refresh du cache d'enrichissement
     * @param {String} source - 'knox', 'strapp' ou null pour tout effacer
     */
    clearEnrichmentCache({ commit }, source = null) {
      commit('clearEnrichmentCache', source)
      console.log(`[Store] Enrichment cache cleared${source ? ` for ${source}` : ''}`)
    },

    /**
     * Nettoie uniquement les entrées expirées du cache
     */
    cleanupExpiredCache({ commit }) {
      commit('cleanupExpiredCache')
      console.log('[Store] Expired cache entries cleaned up')
    }
  },

  getters: {
    allDevices: state => state.allDevices,
    selectedDevice: state => state.selectedDevice,
    devicesLoading: state => state.devicesLoading,
    devicesError: state => state.devicesError,
    
    // Cache helpers
    getCachedEnrichment: state => (imei, source) => {
      const cache = state.enrichmentCache[source]?.[imei]
      if (!cache) return null
      
      const now = Date.now()
      const isExpired = now - cache.timestamp > state.enrichmentTTL
      
      if (isExpired) {
        return null
      }
      
      return cache.data
    },
    
    isEnrichmentInProgress: state => imei => {
      return state.enrichmentInProgress.has(imei)
    },
    
    shouldEnrich: (state, getters) => (imei, source) => {
      // Ne pas enrichir si déjà en cours
      if (getters.isEnrichmentInProgress(imei)) {
        return false
      }
      
      // Enrichir si pas de cache ou cache expiré
      const cached = getters.getCachedEnrichment(imei, source)
      return cached === null
    },
    
    knoxDevices: state => state.knoxDevices,
    knoxGroups: state => state.knoxGroups,
    knoxUsers: state => state.knoxUsers,
    knoxAppsCatalog: state => state.knoxAppsCatalog,
    knoxProfiles: state => state.knoxProfiles,
    
    strappDevices: state => state.strappDevices,
    strappModels: state => state.strappModels,
    strappSites: state => state.strappSites,
    strappBillingEntities: state => state.strappBillingEntities,
    strappUsers: state => state.strappUsers,
    strappSIMs: state => state.strappSIMs,
    strappSubscriptions: state => state.strappSubscriptions,
    strappAccessories: state => state.strappAccessories,
    strappOrders: state => state.strappOrders,
    
    // SIM usage
    simUsageMap: state => state.simUsageMap,
    simUsageLoading: state => state.simUsageLoading,
    simUsageProgress: state => state.simUsageProgress,
    simUsageLastUpdate: state => state.simUsageLastUpdate,
    
    deviceFilters: state => state.deviceFilters,
    deviceDialog: state => state.deviceDialog,
    deviceActionDialog: state => state.deviceActionDialog,
    selectedAction: state => state.selectedAction,
    actionLoading: state => state.actionLoading,

    // Computed getters
    filteredDevices: state => {
      const devices = state.allDevices
      const filters = state.deviceFilters

      return devices.filter(device => {
        if (filters.status && device.status !== filters.status) return false
        if (filters.user && device.user?.id !== filters.user) return false
        if (filters.site && device.site?.id !== filters.site) return false
        if (filters.model && device.model?.id !== filters.model) return false
        return true
      })
    },

    devicesWithKnoxData: state => {
      return state.allDevices.filter(device => device.knoxData !== null)
    },

    devicesWithStrappData: state => {
      return state.allDevices.filter(device => device.strappData !== null)
    },

    devicesWithBothData: state => {
      return state.allDevices.filter(device => 
        device.knoxData !== null && device.strappData !== null
      )
    },

    /**
     * SIMs enrichis avec leur consommation
     */
    simsWithUsage: state => {
      return state.strappSIMs.map(sim => {
        const usage = state.simUsageMap[sim.id]
        const totalDataMo = usage?.totalDataMo || 0
        const subscriptionDataMo = sim.subscription?.dataMo || 0
        const subscriptionDataPackage = sim.subscription?.dataPackage || 0
        const subscriptionName = sim.subscription?.name || ''
        const hasGoInName = /\b(Go|GB)\b/i.test(subscriptionName)
        const quotaMo = subscriptionDataPackage > 0
          ? subscriptionDataPackage * 1000
          : (subscriptionDataMo > 0
            ? (hasGoInName && subscriptionDataMo <= 100 ? subscriptionDataMo * 1000 : subscriptionDataMo)
            : (sim.quotaMo || 0))
        
        const usagePercentage = quotaMo > 0 
          ? (totalDataMo / quotaMo) * 100 
          : 0

        return {
          ...sim,
          usage: {
            totalDataMo,
            usagePercentage: Math.round(usagePercentage * 100) / 100,
            hasAlert: usagePercentage >= 80
          },
          quotaMo
        }
      })
    },

    /**
     * SIMs avec alerte (≥80%)
     */
    alertSims: (state, getters) => {
      return getters.simsWithUsage
        .filter(sim => sim.usage.hasAlert)
        .sort((a, b) => b.usage.usagePercentage - a.usage.usagePercentage)
    },

    /**
     * Sites avec alertes groupés
     */
    sitesWithAlerts: (state, getters) => {
      const alertSims = getters.alertSims
      const siteMap = {}

      // Lookup rapide des sites par ID
      const sitesLookup = (state.strappSites || []).reduce((acc, s) => {
        if (s.id) acc[s.id] = s
        return acc
      }, {})

      alertSims.forEach(sim => {
        const rawSite = sim.site
        let siteId, siteName

        if (rawSite && typeof rawSite === 'object') {
          siteId = rawSite.id || rawSite._id || '__unknown__'
          siteName = rawSite.name || rawSite.label || rawSite.shopName || `Site ${siteId}`
        } else if (rawSite) {
          siteId = rawSite
          const found = sitesLookup[rawSite]
          siteName = found ? (found.name || found.label || found.shopName || `Site ${rawSite}`) : `Site ${rawSite}`
        } else {
          siteId = '__unknown__'
          siteName = 'Site non renseigné'
        }

        if (!siteMap[siteId]) {
          siteMap[siteId] = {
            siteId,
            siteName,
            alertSims: [],
            maxUsagePercent: 0
          }
        }

        siteMap[siteId].alertSims.push(sim)
        if (sim.usage.usagePercentage > siteMap[siteId].maxUsagePercent) {
          siteMap[siteId].maxUsagePercent = Math.round(sim.usage.usagePercentage * 100) / 100
        }
      })

      return Object.values(siteMap)
        .map(site => ({
          ...site,
          sortedAlertSims: [...site.alertSims].sort((a, b) => b.usage.usagePercentage - a.usage.usagePercentage)
        }))
        .sort((a, b) => b.maxUsagePercent - a.maxUsagePercent)
    }
  }
}
